import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	forwardRef,
	Input,
	OnInit,
	Output,
} from '@angular/core';
import { BaseComponent } from '@core/base.component';
import * as utils from '@globalUtils/utilities';
import { Address } from '@models/address';
import { CloserTablerIcon, ICON } from '@core/util/icon';
import { TestHooks } from './address.hooks';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export enum DisplayState {
	EDIT = 'MANUAL',
	CARD = 'CARD',
}

interface VM {
	address: Address;
	displayCountryTop: boolean;
	enableCountrySelect: boolean;
	name: string;
	showCareOf: boolean;
	state: DisplayState;
}

const defaultVM: VM = {
	address: null,
	displayCountryTop: false,
	enableCountrySelect: true,
	name: 'Address',
	showCareOf: false,
	state: DisplayState.CARD,
};

@Component({
	selector: 'app-address',
	templateUrl: './address.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => AddressComponent),
			multi: true,
		},
	],
	standalone: false,
})
export class AddressComponent
	extends BaseComponent<VM>
	implements OnInit, ControlValueAccessor
{
	testHooks = TestHooks;
	_disabled: boolean;

	@Input() set enableCountrySelect(enableCountrySelect: boolean) {
		this.VM = {
			enableCountrySelect,
		};
	}

	@Input() set displayCountryTop(displayCountryTop: boolean) {
		this.VM = {
			displayCountryTop,
		};
	}

	@Input() set name(name: string) {
		this.VM = {
			name,
		};
	}
	@Input() state: DisplayState = DisplayState.EDIT;
	@Input() labelIcon: ICON | CloserTablerIcon;
	@Input() labelToolTip: string;
	@Input() labelIconClasses: string;
	@Input() set disabled(disabled: boolean) {
		this._disabled = disabled;
	}

	@Input() skeletonLoading = false;
	@Input() required: boolean;
	@Input() showCareOf: boolean;
	@Input() submitted: boolean;
	@Output() onAddressUpdate: EventEmitter<Address> = new EventEmitter();

	public DisplayState = DisplayState;
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public onChange = (address: Address) => {};
	public onTouched = () => {};
	get disabled() {
		return this._disabled;
	}
	get address() {
		return this.VM.address;
	}

	constructor() {
		super(defaultVM);
	}
	registerOnChange(fn: (address: Address) => void): void {
		this.onChange = fn;
	}
	registerOnTouched(fn: VoidFunction): void {
		this.onTouched = fn;
	}
	setDisabledState?(isDisabled: boolean): void {
		this._disabled = isDisabled;
	}
	writeValue(address1: Address): void {
		this.address = address1;
	}

	ngOnInit(): void {
		this.VM = {
			state:
				this.VM.state !== DisplayState.EDIT ? DisplayState.CARD : this.VM.state,
		};

		if (
			utils.isSet(this.VM?.address?.street) ||
			this.state === DisplayState.CARD
		) {
			this.VM = { state: DisplayState.CARD };
		} else {
			this.VM = { state: DisplayState.EDIT };
		}
	}

	@Input() set address(address1: Address) {
		this.onChange(address1);
		this.VM = {
			address: {
				...address1,
				country: address1?.country || 'Canada',
				province: address1?.province || 'ON',
				street: address1?.street || '',
			},
		};
	}

	onAddressUpdated(address: Address) {
		this.VM = {
			address,
		};
		this.onChange(address);

		this.onAddressUpdate.emit(address);
	}

	onAddressSearchUpdated(address: Address) {
		this.VM = {
			address,
		};
		this.onChange(address);

		this.onAddressUpdate.emit(address);
	}

	edit() {
		this.VM = {
			state: DisplayState.EDIT,
		};
	}

	remove() {
		this.VM = {
			address: null,
			state: DisplayState.EDIT,
		};
		this.onChange(null);

		this.onAddressUpdate.emit(null);
	}

	protected readonly ICON = ICON;
}
